import React, { useState, useEffect } from 'react';
import { 
  Clock, CheckCircle, MessageCircle, FileText, AlertCircle, Loader2,
  Upload, Calendar, Phone, Mail, Search, BookOpen, Edit, Users,
  Briefcase, Video, PenTool, ChevronRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActivityTimeline = ({ caseId }) => {
  const { i18n } = useTranslation('caseWorkspace');
  const currentLanguage = i18n.language?.split('-')[0] || 'en';
  
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();
  }, [caseId, currentLanguage]);

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/lexy/activities/${caseId}?lang=${currentLanguage}`);
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (action) => {
    const icons = {
      'TIME_LOGGED': Clock,
      'TASK_CREATED': CheckCircle,
      'TASK_COMPLETED': CheckCircle,
      'MESSAGE_SENT': MessageCircle,
      'EMAIL_SENT': Mail,
      'DOCUMENT_UPLOADED': Upload,
      'DOCUMENT_REVIEWED': FileText,
      'NOTE_ADDED': PenTool,
      'STATUS_CHANGED': AlertCircle,
      'CLIENT_COMMUNICATION': Users,
      'CLIENT_CALL': Phone,
      'MEETING_SCHEDULED': Video,
      'LEGAL_RESEARCH': Search,
      'RESEARCH_CONDUCTED': BookOpen,
      'LEGAL_ANALYSIS': Briefcase,
      'DOCUMENT_EDITED': Edit,
    };
    return icons[action] || Clock;
  };

  const formatTimestamp = (activity) => {
    // Try created_at first, then timestamp
    const dateStr = activity.created_at || activity.timestamp;
    
    if (!dateStr) return currentLanguage === 'en' ? 'Unknown' : 'Ukjent';
    
    try {
      const date = new Date(dateStr);
      
      // Check for invalid date
      if (isNaN(date.getTime())) {
        return currentLanguage === 'en' ? 'Recently' : 'Nylig';
      }
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return currentLanguage === 'en' ? 'Just now' : 'Akkurat nå';
      if (diffMins < 60) return currentLanguage === 'en' ? `${diffMins}m ago` : `${diffMins}m siden`;
      if (diffHours < 24) return currentLanguage === 'en' ? `${diffHours}h ago` : `${diffHours}t siden`;
      if (diffDays === 1) return currentLanguage === 'en' ? 'Yesterday' : 'I går';
      if (diffDays < 7) return currentLanguage === 'en' ? `${diffDays}d ago` : `${diffDays}d siden`;
      
      return date.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'nb-NO', {
        day: 'numeric',
        month: 'short'
      });
    } catch (e) {
      return currentLanguage === 'en' ? 'Recently' : 'Nylig';
    }
  };

  // Group activities by date
  const groupedActivities: Record<string, any[]> = activities.reduce((groups: Record<string, any[]>, activity) => {
    const dateStr = activity.created_at || activity.timestamp;
    let date = 'Unknown';
    
    if (dateStr) {
      try {
        const d = new Date(dateStr);
        if (!isNaN(d.getTime())) {
          date = d.toDateString();
        }
      } catch (e) {
        // Keep default 'Unknown'
      }
    }
    
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(activity);
    return groups;
  }, {});

  const getDateLabel = (dateStr) => {
    if (dateStr === 'Unknown') {
      return currentLanguage === 'en' ? 'Recent Activity' : 'Nylig aktivitet';
    }
    
    try {
      const date = new Date(dateStr);
      
      if (isNaN(date.getTime())) {
        return currentLanguage === 'en' ? 'Recent Activity' : 'Nylig aktivitet';
      }
      
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (date.toDateString() === today.toDateString()) {
        return currentLanguage === 'en' ? 'Today' : 'I dag';
      }
      if (date.toDateString() === yesterday.toDateString()) {
        return currentLanguage === 'en' ? 'Yesterday' : 'I går';
      }
      return date.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'nb-NO', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      });
    } catch (e) {
      return currentLanguage === 'en' ? 'Recent Activity' : 'Nylig aktivitet';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
          <Clock className="w-6 h-6 text-slate-400" />
        </div>
        <h3 className="text-base font-medium text-slate-900 mb-1">
          {currentLanguage === 'en' ? 'No activity yet' : 'Ingen aktivitet ennå'}
        </h3>
        <p className="text-sm text-slate-500">
          {currentLanguage === 'en' 
            ? 'Activities will appear here as you work on the case' 
            : 'Aktiviteter vil vises her når du jobber med saken'}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {currentLanguage === 'en' ? 'Activity' : 'Aktivitet'}
          </h2>
          <p className="text-sm text-slate-500">
            {activities.length} {currentLanguage === 'en' ? 'events' : 'hendelser'}
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-8">
        {Object.entries(groupedActivities).map(([dateStr, dateActivities]) => (
          <div key={dateStr}>
            {/* Date label */}
            <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-4">
              {getDateLabel(dateStr)}
            </div>
            
            {/* Activities list */}
            <div className="space-y-1">
              <AnimatePresence>
                {dateActivities.map((activity, index) => {
                  const Icon = getActivityIcon(activity.action);
                  
                  return (
                    <motion.div
                      key={activity.id || `activity-${index}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className="group flex items-start gap-4 py-3 px-3 -mx-3 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      {/* Icon */}
                      <div className="flex-shrink-0 w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Icon className="w-4 h-4 text-slate-500" />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900 leading-snug">
                          {activity.description}
                        </p>
                        
                        {/* Metadata */}
                        {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                          <div className="flex flex-wrap items-center gap-3 mt-1.5">
                            {activity.metadata.document_name && (
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {activity.metadata.document_name}
                              </span>
                            )}
                            {activity.metadata.duration && (
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {activity.metadata.duration}
                              </span>
                            )}
                            {activity.metadata.recipient && (
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {activity.metadata.recipient}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Timestamp */}
                      <div className="flex-shrink-0">
                        <span className="text-xs text-slate-400">
                          {formatTimestamp(activity)}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityTimeline;
